import {
    Component,
    ViewEncapsulation,
    OnInit,
    inject,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { CommonModule } from "@angular/common";
import { CerrixTreeItemPickerSingleComponent } from "@app/shared/organisms/cerrix-tree-item-picker-single/cerrix-tree-item-picker-single.component";
import { CerrixUserPickerComponent } from "@app/shared/organisms/cerrix-user-picker/cerrix-user-picker.component";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { HttpClient } from "@angular/common/http";
import { finalize, firstValueFrom, Observable } from "rxjs";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import {
    CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
    CerrixSlideOutEditItemSection,
} from "@app/shared/organisms/cerrix-slide-out-edit-item/models/cerrix-slide-out-edit-item-section.model";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { OwnershipInformationModel } from "@models/assetmanagement/ownershipinformationmodel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import { CerrixInputValidationRequiredDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-required.directive";

@Component({
    selector: "app-edit-asset-ownership-information",
    templateUrl: "./edit-asset-ownership-information.component.html",
    styleUrls: ["./edit-asset-ownership-information.component.scss"],
    imports: [
        CommonModule,
        TranslatePipe,
        CerrixInstructionsIconComponent,
        CerrixButtonComponent,
        CerrixTreeItemPickerSingleComponent,
        CerrixUserPickerComponent,
        CerrixInputValidationRequiredDirective,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [
        CerrixInputValidationRegistryService,
        {
            provide: CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
            useExisting: EditAssetOwnershipInformationComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class EditAssetOwnershipInformationComponent
    implements OnInit, CerrixSlideOutEditItemSection
{
    @Input() model: AssetManagementModel;

    @Output() modelChange: EventEmitter<AssetManagementModel> =
        new EventEmitter<AssetManagementModel>();
    @Output() closeEdit: EventEmitter<void> = new EventEmitter<void>();

    protected editModel: OwnershipInformationModel;
    protected organizationsList: CerrixTreeItem[] = [];
    protected userList: IdGuidNameCombination[] = [];

    protected editSaving = false;

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _toastService = inject(ToastrService);
    private _standingDataDS = inject(StandingdataDataService);
    private _http = inject(HttpClient);

    async ngOnInit() {
        // Before the await: the slide-out can call hasUnsavedChanges() while the scope loads.
        this.revertChanges();

        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();

        this.organizationsList = await firstValueFrom(
            this._standingDataDS.getOrganizationsIncludingSelected(
                ModuleType.AssetManagement,
                this.model.ownershipInformation.owningOrganizationId,
                true,
            ),
        );

        this.userList = await this.getUsers();
    }

    private async getUsers(): Promise<IdGuidNameCombination[]> {
        let params = "";

        if (this.model.ownershipInformation.ownerId) {
            params = `?selected=${this.model.ownershipInformation.ownerId}`;
        }

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/list` + params),
        );
    }

    public async save(): Promise<boolean> {
        if (!this.validateFields()) {
            return false;
        }

        this.editSaving = true;
        const savingPrompt = this._promptService.loader(
            this._translation.t("detail.savingChanges"),
        );

        const storedAsset = await firstValueFrom(
            this.editSaveSection().pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.editSaving = false;
                    this._tabService.refresh(this._pages.AssetManagementOverview);
                }),
            ),
        );

        if (storedAsset?.guid) {
            this.model = Object.assign(new AssetManagementModel(), storedAsset, {
                ownershipInformation: {
                    ownerId: storedAsset.ownershipInformation?.ownerId,
                    owningOrganizationId: storedAsset.ownershipInformation?.owningOrganizationId,
                },
            });
            this.modelChange.emit(this.model);
            this.revertChanges();

            this._toastService.success(
                this._translation.t("detail.updateCompleted"),
            );
            return true;
        }
        return false;
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._translation.t(
                                "detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._translation.t("detail.saveFailed"),
            );
            return false;
        }
        return true;
    }

    public hasUnsavedChanges(): boolean {
        return (
            this.model.ownershipInformation?.owningOrganizationId !==
                this.editModel.owningOrganizationId ||
            (this.model.ownershipInformation?.ownerId ?? null) !== (this.editModel.ownerId ?? null)
        );
    }

    private editSaveSection(): Observable<AssetProjection> {
        return this._ds.vbetaRegisterOwnership(this.model.guid, {
            owningOrganizationId: this.editModel.owningOrganizationId,
            ownerId: this.editModel.ownerId ?? null,
            version: this.model.version,
        });
    }

    private revertChanges(): void {
        this.editModel = {
            ownerId: this.model.ownershipInformation?.ownerId,
            owningOrganizationId: this.model.ownershipInformation?.owningOrganizationId,
        };
    }
}
